import React, { useState, useEffect } from "react";
import { FaTrash, FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import {
  getLoginHistoryApi,
  deleteLoginHistoryApi,
} from "../../../apis/payments.api";
import { useDarkMode } from "../../../hooks/useDarkMode";

const LoginHistory = () => {
  const { isDarkMode } = useDarkMode();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    fetchLoginHistory(currentPage);
  }, [currentPage]);

  const fetchLoginHistory = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      const res = await getLoginHistoryApi(page, pageSize);
      if (res.data?.results) {
        setHistory(res.data.results);
        setTotalCount(res.data.count || 0);
      } else if (Array.isArray(res.data)) {
        setHistory(res.data);
        setTotalCount(res.data.length);
      } else {
        setHistory([]);
        setTotalCount(0);
      }
    } catch (err) {
      setError("Failed to load login history");
      setHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteHistory = async (historyId) => {
    if (window.confirm("Are you sure you want to delete this login record?")) {
      try {
        setDeleting(historyId);
        await deleteLoginHistoryApi(historyId);
        await fetchLoginHistory(currentPage);
        setDeleting(null);
      } catch (err) {
        setError("Failed to delete login record");
        setDeleting(null);
      }
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  if (loading) {
    return (
      <div
        className={`text-center py-8 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
      >
        Loading login history...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <div
          className={`bg-${isDarkMode ? "red-900" : "red-50"} border border-${isDarkMode ? "red-700" : "red-200"} text-${isDarkMode ? "red-200" : "red-700"} px-4 py-3 rounded`}
        >
          {error}
        </div>
      )}

      <div className="overflow-x-auto">
        <table
          className={`w-full text-sm ${isDarkMode ? "bg-gray-800 text-gray-200" : "bg-white text-gray-900"}`}
        >
          <thead>
            <tr
              className={`border-b ${isDarkMode ? "border-gray-700 bg-gray-900" : "border-gray-200 bg-gray-50"}`}
            >
              <th
                className={`text-left px-4 py-3 font-semibold ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
              >
                Device
              </th>
              <th
                className={`text-left px-4 py-3 font-semibold ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
              >
                IP Address
              </th>
              <th
                className={`text-left px-4 py-3 font-semibold ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
              >
                Status
              </th>
              <th
                className={`text-left px-4 py-3 font-semibold ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
              >
                Date & Time
              </th>
              <th
                className={`text-center px-4 py-3 font-semibold ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
              >
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            {history && history.length > 0 ? (
              history.map((log) => (
                <tr
                  key={log.id}
                  className={`border-b ${isDarkMode ? "border-gray-700 hover:bg-gray-700" : "border-gray-100 hover:bg-gray-50"}`}
                >
                  <td
                    className={`px-4 py-3 font-medium ${isDarkMode ? "text-gray-200" : "text-gray-900"}`}
                  >
                    {log.device_name}
                  </td>
                  <td
                    className={`px-4 py-3 font-mono text-xs ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                  >
                    {log.ip_address}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {log.status === "success" ? (
                        <>
                          <FaCheckCircle
                            className={
                              isDarkMode ? "text-green-400" : "text-green-500"
                            }
                          />
                          <span
                            className={`font-medium ${isDarkMode ? "text-green-300" : "text-green-600"}`}
                          >
                            Success
                          </span>
                        </>
                      ) : (
                        <>
                          <FaTimesCircle
                            className={
                              isDarkMode ? "text-red-400" : "text-red-500"
                            }
                          />
                          <span
                            className={`font-medium ${isDarkMode ? "text-red-300" : "text-red-600"}`}
                          >
                            Failed
                          </span>
                        </>
                      )}
                    </div>
                  </td>
                  <td
                    className={`px-4 py-3 text-xs ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                  >
                    {log.timestamp
                      ? new Date(log.timestamp).toLocaleString("en-US", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                        })
                      : "N/A"}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => handleDeleteHistory(log.id)}
                      disabled={deleting === log.id}
                      className={`hover:${isDarkMode ? "text-red-300" : "text-red-700"} ${isDarkMode ? "text-red-400" : "text-red-500"} disabled:opacity-50`}
                      title="Delete login record"
                    >
                      <FaTrash className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan="5"
                  className={`text-center py-8 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  No login history available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className={`px-3 py-1 border ${isDarkMode ? "border-gray-600" : "border-gray-300"} rounded disabled:opacity-50 disabled:cursor-not-allowed hover:${isDarkMode ? "bg-gray-700" : "bg-gray-50"}`}
          >
            Previous
          </button>

          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            const pageNum = i + 1;
            return (
              <button
                key={pageNum}
                onClick={() => setCurrentPage(pageNum)}
                className={`px-3 py-1 rounded ${
                  currentPage === pageNum
                    ? "bg-blue-600 text-white"
                    : `border ${isDarkMode ? "border-gray-600 hover:bg-gray-700" : "border-gray-300 hover:bg-gray-50"}`
                }`}
              >
                {pageNum}
              </button>
            );
          })}

          <button
            onClick={() =>
              setCurrentPage(Math.min(totalPages, currentPage + 1))
            }
            disabled={currentPage === totalPages}
            className={`px-3 py-1 border ${isDarkMode ? "border-gray-600" : "border-gray-300"} rounded disabled:opacity-50 disabled:cursor-not-allowed hover:${isDarkMode ? "bg-gray-700" : "bg-gray-50"}`}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default LoginHistory;
